import { AnimatePresence, motion } from 'framer-motion';
import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { DappsPage } from '../pages/DappsPage';
import { HubspotManagementPage } from '../pages/HubspotManagementPage';
import { PublishersPage } from '../pages/PublishersPage';
import { ReportingPage } from '../pages/ReportingPage';
import { UserManagementPage } from '../pages/UserManagementPage';
import { DashboardAlerts } from './dashboard/DashboardAlerts';
import { DashboardAnalytics } from './dashboard/DashboardAnalytics';
import { DashboardHome } from './dashboard/DashboardHome';
import { DashboardMain } from './dashboard/DashboardMain';
import { DeveloperApiKeysPage } from './DeveloperApiKeysPage';
import { ErrorBoundary } from './ErrorBoundary';
import { DashboardLoadingState } from './LoadingStates';
import { MobileHeader } from './MobileHeader';
import { MobileSidebar } from './MobileSidebar';
import { PreferencesSettingsPage } from './PreferencesSettingsPage';
import { ProfileSettingsPage } from './ProfileSettingsPage';
import { PublisherFrozenBanner } from './PublisherFrozenBanner';
import { PublisherVerificationBanner } from './PublisherVerificationBanner';
import { PublisherProfilePage } from './PublisherProfilePage';
import { SubmitKybPage } from './SubmitKybPage';
import { SubmitKycPage } from './SubmitKycPage';
import { Sidebar } from './sidebar/Sidebar';
import { SidebarDataProvider } from './sidebar/hooks/useSidebarData';
import { TeamSettingsPage } from './TeamSettingsPage';
import { getDappRouteSegmentFromPath } from '../utils/dappRoutes';

const ROUTE_COMPONENTS = {
  '/dashboard': DashboardMain,
  '/dashboard/': DashboardMain,
  '/dashboard/home': DashboardHome,
  '/dashboard/analytics': DashboardAnalytics,
  '/dashboard/details': DashboardMain,
  '/dashboard/reviews': DashboardMain,
  '/dashboard/alerts': DashboardAlerts,
  '/dashboard/submit-kyc': SubmitKycPage,
  '/dashboard/submit-kyb': SubmitKybPage,
  '/dashboard/settings/api-keys': DeveloperApiKeysPage,
  '/dashboard/settings/team': TeamSettingsPage,
  '/dashboard/settings/profile': ProfileSettingsPage,
  '/dashboard/settings/preferences': PreferencesSettingsPage,
  '/dashboard/settings/publisher': PublisherProfilePage,
  '/reporting': ReportingPage,
  '/tickets': HubspotManagementPage,
  '/tickets/': HubspotManagementPage,
  '/super/users': UserManagementPage,
  '/dapps': DappsPage,
  '/dapps/': DappsPage,
  '/publishers': PublishersPage,
  '/publishers/': PublishersPage,
} as const;

const DRAWER_ROUTES = ['/new-dapp', '/new-version'] as const;

const DRAWER_ANIMATION = {
  scale: { open: 0.95, closed: 1 },
  opacity: { open: 0.4, closed: 1 },
  transition: {
    duration: 0.2,
    ease: [0.25, 0.1, 0.25, 1] as const,
  },
  spring: {
    type: 'spring' as const,
    damping: 30,
    stiffness: 300,
    mass: 0.8,
  },
} as const;

export const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isDrawerOpen = DRAWER_ROUTES.some((route) =>
    location.pathname.endsWith(route),
  );
  const previousLocation = useRef<string>('/dashboard');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const handleClose = () => {
    navigate(previousLocation.current);
  };

  useEffect(() => {
    if (!isDrawerOpen) {
      previousLocation.current = `${location.pathname}${location.search}${location.hash}`;
    }
  }, [location.hash, location.pathname, location.search, isDrawerOpen]);

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          navigate(previousLocation.current);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [isDrawerOpen, navigate]);

  const renderBackgroundContent = () => {
    const path = previousLocation.current;
    const pathname = path.split(/[?#]/, 1)[0];

    const dappSegment = getDappRouteSegmentFromPath(pathname);
    if (dappSegment) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400">Loading dApp {dappSegment}...</div>
        </div>
      );
    }

    const Component =
      ROUTE_COMPONENTS[pathname as keyof typeof ROUTE_COMPONENTS];
    return Component ? <Component /> : <DashboardMain />;
  };

  return (
    <ErrorBoundary>
      <SidebarDataProvider>
        <div className="flex flex-col h-screen relative overflow-hidden">
          <PublisherVerificationBanner />

          <div className="flex min-h-0 flex-1">
            <div className="hidden lg:block h-full">
              <Sidebar />
            </div>

            <MobileSidebar
              isOpen={isMobileSidebarOpen}
              onClose={() => setIsMobileSidebarOpen(false)}
            >
              <Sidebar
                className="w-full h-full bg-transparent"
                onLinkClick={() => setIsMobileSidebarOpen(false)}
              />
            </MobileSidebar>

            <div className="flex-1 flex flex-col h-full min-w-0">
              <MobileHeader onMenuClick={() => setIsMobileSidebarOpen(true)} />
              <PublisherFrozenBanner />

              <motion.div
                animate={{
                  scale: isDrawerOpen
                    ? DRAWER_ANIMATION.scale.open
                    : DRAWER_ANIMATION.scale.closed,
                  opacity: isDrawerOpen
                    ? DRAWER_ANIMATION.opacity.open
                    : DRAWER_ANIMATION.opacity.closed,
                }}
                transition={DRAWER_ANIMATION.transition}
                className={`flex flex-1 flex-col overflow-auto bg-[#161717] md:m-2 md:rounded-xl md:border md:border-neutral-800 ${isDrawerOpen ? 'pointer-events-none' : ''}`}
              >
                <ErrorBoundary>
                  <Suspense fallback={<DashboardLoadingState />}>
                    {isDrawerOpen ? renderBackgroundContent() : <Outlet />}
                  </Suspense>
                </ErrorBoundary>
              </motion.div>
            </div>
          </div>

          <AnimatePresence>
            {isDrawerOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="fixed inset-0 bg-black/50 z-40"
                  onClick={handleClose}
                />

                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={DRAWER_ANIMATION.spring}
                  className="fixed inset-x-0 bottom-0 h-full z-50"
                >
                  <div className="h-full bg-neutral-950 shadow-2xl overflow-hidden">
                    <Outlet />
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </SidebarDataProvider>
    </ErrorBoundary>
  );
};
